import {Alert, Box, Loader, Stack, Text, TextInput} from '@mantine/core';
import React, {useEffect} from 'react';

import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MSA_SUPPORT} from 'model-hub/scientific-models/ScientificModelConstants';
import {
  ScientificModelRegistry,
  useScientificModelRegistry,
} from 'model-hub/scientific-models/useAvailableScientificModels';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {GetPipelineConfigSpecType, isStringSpec} from 'pipelines/StepShapeUtils';
import getTestProps from 'test/lib/cypress/getTestProps';

import FolderEditor from '../editors/FolderEditor';
import StepConfigFieldsTemplate from '../editors/StepConfigFieldsTemplate';
import useTaggedUnionFields from '../editors/useTaggedUnionFields';
import {maybeGetValueFromConfig} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  FieldUISchemaWithStandardComponent,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  StepConfigEditorOverride,
} from '../StepConfigEditorTypes';
import {runScientificModelConfigOnChange} from './runScientificModelConfigOnChange';
import {getModelInputFields, USE_MSA_DISPLAY_NAME, USE_MSA_KEY} from './runScientificModelInputFields';

const MODEL_CONFIG_KEY = 'modelConfig';
const PREDICTION_BATCH_FOLDER_ID = 'predictionBatchFolderId';
const PREDICTION_BATCH_NAME = 'predictionBatchName';
const PREDICTION_BATCH_NAME_MAX_LENGTH = 255;

const MODEL_CONFIG_SHAPE_V0 =
  PIPELINE_STEP_SHAPES[PipelineStepType.RUN_SCIENTIFIC_MODEL][0].config.modelConfig;

type ModelConfigShapeBuilder = (
  registry: ScientificModelRegistry,
  selectedModel: unknown
) => GetPipelineConfigSpecType<'taggedUnion'>;

function ScientificModelConfigEditor({
  getModelConfigShape,
  ...props
}: CustomComponentEditorProps & {getModelConfigShape: ModelConfigShapeBuilder}): JSX.Element {
  const scientificModelRegistry = useScientificModelRegistry();

  let component: JSX.Element;
  if (scientificModelRegistry.type === 'loading') {
    component = <Loader size="sm" role="status" aria-label="Loading models" />;
  } else if (scientificModelRegistry.type === 'error') {
    component = (
      <Alert color="red" role="alert">
        Unable to load models. Please refresh the page.
      </Alert>
    );
  } else {
    component = (
      <LoadedScientificModelConfigEditor
        {...props}
        registry={scientificModelRegistry.registry}
        getModelConfigShape={getModelConfigShape}
      />
    );
  }

  return (
    <Box {...getTestProps({component: 'ScientificModelConfigEditor', element: 'root'})}>{component}</Box>
  );
}

function LoadedScientificModelConfigEditor({
  config,
  onChangeConfig,
  disabled,
  registry,
  getModelConfigShape,
}: CustomComponentEditorProps & {
  registry: ScientificModelRegistry;
  getModelConfigShape: ModelConfigShapeBuilder;
}): JSX.Element {
  const predictionBatchFolderId = maybeGetValueFromConfig(config, [PREDICTION_BATCH_FOLDER_ID]) as
    | string
    | null;
  const selectedModel = maybeGetValueFromConfig(config, [MODEL_CONFIG_KEY, 'model']) as string | null;
  const selectedModelNode =
    selectedModel == null ? undefined : registry.catalogByExternalReference.get(selectedModel);

  // Set default value of prediction batch folder on initial load if no model is selected
  useEffect(() => {
    const defaultFolderId = FolderDefaults.get(FolderDefaultsKey.BULK_MODEL_PREDICTION);
    if (!disabled && selectedModel == null && predictionBatchFolderId == null && defaultFolderId != null) {
      onChangeConfig(config, [PREDICTION_BATCH_FOLDER_ID], defaultFolderId);
    }
  }, [config, disabled, selectedModel, onChangeConfig, predictionBatchFolderId]);

  const predictionBatchName =
    (maybeGetValueFromConfig(config, [PREDICTION_BATCH_NAME]) as string | null) ?? '';

  const modelConfigShape = getModelConfigShape(registry, selectedModel);
  const modelDisplayNames = Object.fromEntries(
    modelConfigShape.choices.map((choice) => {
      const model = getModelValue(choice);
      return [model, registry.catalogByExternalReference.get(model)?.displayName ?? model];
    })
  );

  const inputFields = (selectedModelNode != null ? getModelInputFields(selectedModelNode) : null) ?? [];
  const msaRequired = selectedModelNode?.capabilities.msaSupport === MSA_SUPPORT.REQUIRED;
  const uiSchemas = inputFields.map((field): [string, FieldUISchemaWithStandardComponent] => {
    let schema: FieldUISchemaWithStandardComponent = {
      displayName: field.displayName,
      configShapePath: [field.key],
      required: field.spec.required,
    };
    if (field.key === USE_MSA_KEY) {
      schema = {
        ...schema,
        editorOverride: StepConfigEditorOverride.CHECKBOX,
        // Required-MSA models always run with MSAs regardless of the saved value.
        fieldSettings: {checkboxLabel: USE_MSA_DISPLAY_NAME, ...(msaRequired ? {defaultValue: true} : {})},
        disableFn: () => msaRequired,
      };
    }
    return [field.key, schema];
  });

  const {tagField, choiceFields} = useTaggedUnionFields({
    config,
    configShape: modelConfigShape,
    configShapePath: [MODEL_CONFIG_KEY],
    onChangeConfig,
    disabled,
    fieldSettings: {
      tagKeyDisplayName: 'Model',
      placeholder: 'Select a model',
      tagKeyDisplayNames: modelDisplayNames,
      choiceFieldOrder: inputFields.map((field) => field.key),
      choiceUISchema: Object.fromEntries(uiSchemas),
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: (newValue, _path, oldValue) =>
        runScientificModelConfigOnChange({
          newValue,
          oldValue,
          registry,
        }),
    },
  });

  const predictionBatchFolderField = {
    variableName: 'Prediction batch folder',
    required: true,
    editor: (
      <FolderEditor
        value={predictionBatchFolderId}
        onChange={(folderId) => {
          onChangeConfig(config, [PREDICTION_BATCH_FOLDER_ID], folderId);
          if (folderId != null) {
            FolderDefaults.set(FolderDefaultsKey.BULK_MODEL_PREDICTION, folderId);
          }
        }}
        required={true}
        disabled={disabled}
        showRegistries={false}
        showInventory={false}
        showNotebook={true}
      />
    ),
  };

  const predictionBatchNameField = {
    variableName: 'Prediction batch name',
    required: false,
    editor: (
      <TextInput
        aria-label="Prediction batch name"
        disabled={disabled}
        maxLength={PREDICTION_BATCH_NAME_MAX_LENGTH}
        placeholder="Enter a name, or leave blank to auto-generate one"
        value={predictionBatchName}
        onChange={(event) => {
          const value = event.currentTarget.value;
          onChangeConfig(config, [PREDICTION_BATCH_NAME], value === '' ? null : value);
        }}
        size="lg"
      />
    ),
  };

  if (modelConfigShape.choices.length === 0) {
    return <Text size="sm">No scientific models are available.</Text>;
  }
  return (
    <Stack spacing="sm">
      <Text size="md" mb="md" ml="xs">
        This step runs a scientific model for each input row.
      </Text>
      <Stack spacing={0}>
        <StepConfigFieldsTemplate
          numColumns={1}
          fields={[predictionBatchFolderField, predictionBatchNameField]}
        />
        <StepConfigFieldsTemplate numColumns={1} fields={[{...tagField, required: true}]} />
        {selectedModelNode?.description && (
          <Text size="sm" color="dimmed" mb="lg" ml="xs">
            {selectedModelNode.description}
          </Text>
        )}
        {selectedModel != null && (
          <>
            <StepConfigFieldsTemplate
              numColumns={2}
              fields={choiceFields.filter((field) => field.variableName !== USE_MSA_DISPLAY_NAME)}
            />
            <StepConfigFieldsTemplate
              numColumns={1}
              fields={choiceFields
                .filter((field) => field.variableName === USE_MSA_DISPLAY_NAME)
                .map((field) => ({...field, variableName: undefined}))}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
}

function getModelValue(choice: GetPipelineConfigSpecType<'shape'>): string {
  const model = choice.fields.model;
  if (!isStringSpec(model) || model.value == null) {
    throw new Error('Scientific model config choice must have a fixed model value');
  }
  return model.value;
}

function getV0ModelConfigShape(
  registry: ScientificModelRegistry,
  selectedModel: unknown
): GetPipelineConfigSpecType<'taggedUnion'> {
  return {
    ...MODEL_CONFIG_SHAPE_V0,
    choices: MODEL_CONFIG_SHAPE_V0.choices.filter((choice) => {
      const model = getModelValue(choice);
      return model === selectedModel || registry.availableModelsByExternalReference.has(model);
    }),
  };
}

function getV1ModelConfigShape(
  registry: ScientificModelRegistry,
  selectedModel: unknown
): GetPipelineConfigSpecType<'taggedUnion'> {
  const models = [...registry.availableModels];
  const savedModel =
    typeof selectedModel === 'string' ? registry.catalogByExternalReference.get(selectedModel) : undefined;
  if (
    savedModel != null &&
    !models.some((model) => model.externalReference === savedModel.externalReference)
  ) {
    models.push(savedModel);
  }

  return {
    kind: 'taggedUnion',
    required: true,
    tagKey: 'model',
    choices: models.flatMap((model) => {
      const inputFields = getModelInputFields(model);
      if (inputFields == null) {
        return [];
      }
      return [
        {
          kind: 'shape',
          required: true,
          fields: {
            model: {
              kind: 'string',
              required: true,
              value: model.externalReference,
            },
            ...Object.fromEntries(inputFields.map((field) => [field.key, field.spec])),
          },
        },
      ];
    }),
  };
}

export const RUN_SCIENTIFIC_MODEL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_SCIENTIFIC_MODEL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
  modelConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
    modelConfig: {
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <ScientificModelConfigEditor {...props} getModelConfigShape={getV0ModelConfigShape} />
      ),
    },
  },
};

export const RUN_SCIENTIFIC_MODEL_V1_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_SCIENTIFIC_MODEL,
  1
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
  modelConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
    modelConfig: {
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <ScientificModelConfigEditor {...props} getModelConfigShape={getV1ModelConfigShape} />
      ),
    },
  },
};
